import { useEffect, useMemo, useRef, useState } from "react";
import { BUILDINGS, type BuildingCategory } from "@zend/content";
import { useT } from "../../i18n/index.js";
import { useBalances } from "../../state/balances.js";
import { useSession } from "../../state/session.js";
import { placementValid, useBase } from "../state.js";
import { Sheet } from "./shared.js";
import { BuildingCard } from "./BuildingCard.js";
import { Button } from "../../ui/Button.js";
import { buildCatalogue, readSeen, writeSeen, type CatalogueEntry, type CatalogueGroup } from "./buildCatalogue.js";
import "../building-panels.css";

const CATEGORIES: BuildingCategory[] = ["production", "units", "research", "economy", "defense", "social", "core"];
const GROUPS: CatalogueGroup[] = ["ready", "locked", "built"];
const GROUP_TITLE = { ready: "base.build.ready", locked: "base.build.lockedGroup", built: "base.build.builtGroup" } as const;

type Base = NonNullable<ReturnType<typeof useBase.getState>["base"]>;

/** First free top-left cell for a footprint, scanning the core first (R13 placement). */
export function firstFreeSpot(base: Base, w: number, h: number): { x: number; y: number } | null {
  const rects = [base.grid.core, ...base.zones.filter((z) => z.state === "open").map((z) => z.rect)];
  for (const r of rects) for (let y = r.y; y + h <= r.y + r.h; y++) for (let x = r.x; x + w <= r.x + r.w; x++) if (placementValid(base, x, y, w, h)) return { x, y };
  return null;
}

/** Whether any free footprint exists for a building type; the catalogue and the FAB badge share it. */
export function typeFits(base: Base, buildingId: string): boolean {
  const def = BUILDINGS.find((b) => b.id === buildingId);
  return !!def && def.footprint !== "ring" && firstFreeSpot(base, def.footprint.w, def.footprint.h) !== null;
}

/** The build catalogue: Ready / Unlocks later / Built with the next HQ level's needs first (R10, D65). */
export function BuildMenu() {
  const t = useT();
  const base = useBase((s) => s.base);
  const balances = useBalances((s) => s.balances);
  const accountId = useSession((s) => s.account?.id ?? "anon");
  const setPanel = useBase((s) => s.setPanel);
  const setMode = useBase((s) => s.setMode);
  const setGhost = useBase((s) => s.setGhost);
  const select = useBase((s) => s.select);
  const [cat, setCat] = useState<BuildingCategory | "all">(() => {
    const panel = useBase.getState().panel;
    return panel.kind === "build" && panel.category ? panel.category : "all";
  });
  const [seen] = useState(() => readSeen(accountId));
  const entries = useMemo(() => (base ? buildCatalogue(base, balances, seen, (id) => typeFits(base, id)) : []), [base, balances, seen]);
  const readyIds = useRef<string[]>([]);
  readyIds.current = entries.filter((e) => e.group === "ready").map((e) => e.buildingId);
  // Everything shown as ready counts as seen once the menu closes, so NEW only marks types that appeared since.
  useEffect(() => () => writeSeen(accountId, readyIds.current), [accountId]);
  if (!base) return null;

  const category = (id: string) => BUILDINGS.find((b) => b.id === id)?.category;
  const visible = entries.filter((e) => cat === "all" || category(e.buildingId) === cat);
  const groups = GROUPS.map((g) => ({ g, rows: visible.filter((e) => e.group === g) })).filter((x) => x.rows.length > 0);

  const start = (entry: CatalogueEntry, cover = false) => {
    const def = BUILDINGS.find((b) => b.id === entry.buildingId);
    if (!def || def.footprint === "ring") return;
    const spot = firstFreeSpot(base, def.footprint.w, def.footprint.h);
    setMode({ kind: "place", buildingId: def.id, w: def.footprint.w, h: def.footprint.h, rot: 0, ...(cover ? { cover } : {}) });
    setGhost(spot ? { ...spot, valid: true } : { x: base.grid.core.x, y: base.grid.core.y, valid: false });
    setPanel({ kind: "none" });
  };

  return (
    <Sheet
      title={t("base.build.title")}
      onClose={() => setPanel({ kind: "none" })}
      testId="build-menu"
      wide
      actions={<Button onClick={() => setPanel({ kind: "queue" })} data-testid="build-menu-queues">{t("base.queue.freeBuilders", { n: Math.max(0, base.queues.build.slots - base.queues.build.used) })}</Button>}
    >
      <div className="build-filter" role="tablist">
        <button type="button" role="tab" aria-selected={cat === "all"} className={`chip ${cat === "all" ? "active" : ""}`} onClick={() => setCat("all")} data-testid="cat-all">
          {t("base.build.filterAll")}
        </button>
        {CATEGORIES.map((c) => (
          <button key={c} type="button" role="tab" aria-selected={c === cat} className={`chip ${c === cat ? "active" : ""}`} onClick={() => setCat(c)} data-testid={`cat-${c}`}>
            {t(`cat.${c}`)}
          </button>
        ))}
      </div>
      {groups.length === 0 ? <p className="hint">{t("base.build.empty")}</p> : null}
      {groups.map(({ g, rows }) => (
        <section key={g} className={`build-group build-group-${g}`} data-testid={`build-group-${g}`}>
          <h3>
            {t(GROUP_TITLE[g])} <span className="badge dim">{rows.length}</span>
          </h3>
          <div className="building-catalog">
            {rows.map((e) => (
              <BuildingCard key={e.buildingId} entry={e} nextHq={base.enclave.hqLevel + 1} onBuild={(cover) => start(e, cover)} onOpen={(rowId) => select(rowId)} />
            ))}
          </div>
        </section>
      ))}
    </Sheet>
  );
}
